import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, History, Plus, MessageCircle, Briefcase, Heart, Clock, Dumbbell, DollarSign, Target } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import { realtimeAI } from '../services/realtime-ai';
import { useNavigate } from 'react-router-dom';
import { resourceMatching, CoachingResource } from '../services/resource-matching';
import { ResourcesSidebar } from './ResourcesSidebar';
import { ActionPlanOffer } from './ActionPlanOffer';
import { FormattedMessage } from './FormattedMessage';

interface Message {
  role: 'user' | 'coach';
  content: string;
  resources?: CoachingResource[];
}

interface PendingPlan {
  goal: string;
  actionItems: Array<{ id: string; text: string; status: 'pending' }>;
  timeline?: string;
}

interface TopicPill {
  id: string;
  name: string;
  icon: React.ReactNode;
  category: string;
  starter: string;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  topic_category: string | null;
  preview?: string;
  message_count?: number;
}

const TOPIC_PILLS: TopicPill[] = [
  { id: 'career', name: 'Career', icon: <Briefcase className="h-3.5 w-3.5" />, category: 'career', starter: "I want to explore career options and figure out my path." },
  { id: 'habits', name: 'Habits', icon: <Target className="h-3.5 w-3.5" />, category: 'habits', starter: "I want to break some bad habits and build better ones." },
  { id: 'social', name: 'Social', icon: <Heart className="h-3.5 w-3.5" />, category: 'relationships', starter: "I want to improve my social skills and confidence." },
  { id: 'discipline', name: 'Discipline', icon: <Clock className="h-3.5 w-3.5" />, category: 'personal', starter: "I want to get better at managing my time and staying consistent." },
  { id: 'fitness', name: 'Fitness', icon: <Dumbbell className="h-3.5 w-3.5" />, category: 'health', starter: "I want to start working out and taking better care of myself." },
  { id: 'money', name: 'Money', icon: <DollarSign className="h-3.5 w-3.5" />, category: 'finance', starter: "I want to learn about managing money and building wealth." },
];

export function CoachingHub() {
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversation, setConversation] = useState<Message[]>([]);
  const [message, setMessage] = useState('');
  const [currentResponse, setCurrentResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [matchedResources, setMatchedResources] = useState<CoachingResource[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PendingPlan | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<string | null>(null);
  const [currentTopic, setCurrentTopic] = useState<string | null>(null);

  useEffect(() => {
    const removeListener = realtimeAI.addListener((response) => {
      if (response.type === 'message') {
        setCurrentResponse(prev => prev + response.content);
      } else if (response.type === 'error') {
        setError(response.content);
        setAiLoading(false);
      } else if (response.type === 'plan_offer' && response.planData) {
        setPendingPlan(response.planData);
      } else if (response.type === 'status' && response.content === 'complete') {
        setConversation(prev => [
          ...prev,
          { role: 'coach', content: currentResponse }
        ]);

        if (currentConversationId) {
          saveMessageToDatabase('coach', currentResponse);
        }

        matchResourcesForResponse(currentResponse);

        setCurrentResponse('');
        setAiLoading(false);
      }
    });

    return () => removeListener();
  }, [currentResponse, currentConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, currentResponse]);

  useEffect(() => {
    if (showHistory) {
      loadConversationHistory();
    }
  }, [showHistory, selectedTopicFilter]);

  async function loadConversationHistory() {
    try {
      setLoadingHistory(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('coaching_conversations')
        .select('id, title, created_at, topic_category, message_count')
        .eq('user_id', user.id)
        .order('last_message_at', { ascending: false })
        .limit(20);

      if (selectedTopicFilter) {
        query = query.eq('topic_category', selectedTopicFilter);
      }

      const { data: conversationsData, error } = await query;
      if (error) throw error;

      const conversationsWithPreviews = await Promise.all(
        (conversationsData || []).map(async (conv: any) => {
          const { data: firstMessage } = await supabase
            .from('coaching_messages')
            .select('content')
            .eq('conversation_id', conv.id)
            .eq('role', 'user')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          const preview = firstMessage?.content || 'Empty conversation';

          return {
            ...conv,
            preview: preview.length > 60 ? preview.substring(0, 60) + '...' : preview
          };
        })
      );

      setConversations(conversationsWithPreviews);
    } catch (err) {
      logger.error('Error loading conversation history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function loadConversation(conversationId: string) {
    try {
      setShowHistory(false);

      const { data: messages, error: messagesError } = await supabase
        .from('coaching_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      const { data: conv } = await supabase
        .from('coaching_conversations')
        .select('topic_category')
        .eq('id', conversationId)
        .maybeSingle();

      const loadedMessages: Message[] = (messages || []).map((msg: any) => ({
        role: msg.role,
        content: msg.content,
        resources: msg.resources_shown || []
      }));

      setConversation(loadedMessages);
      setCurrentConversationId(conversationId);
      setCurrentTopic(conv?.topic_category || null);
      setCurrentResponse('');
      setError(null);
      setMatchedResources([]);
    } catch (err) {
      logger.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    }
  }

  async function createNewConversation(topicCategory?: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: newConv, error: convError } = await supabase
        .from('coaching_conversations')
        .insert({
          user_id: user.id,
          topic_category: topicCategory || null
        })
        .select()
        .single();

      if (convError) throw convError;
      setCurrentConversationId(newConv.id);
      setCurrentTopic(topicCategory || null);
      return newConv.id;
    } catch (err) {
      logger.error('Error creating conversation:', err);
      return null;
    }
  }

  async function saveMessageToDatabase(role: 'user' | 'coach', content: string) {
    if (!currentConversationId) return;
    try {
      await supabase
        .from('coaching_messages')
        .insert({
          conversation_id: currentConversationId,
          role: role,
          content: content
        });
    } catch (error) {
      logger.error('Error saving message:', error);
    }
  }

  async function matchResourcesForResponse(responseText: string) {
    try {
      setLoadingResources(true);

      let userDistractions: string[] = [];
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: distractions } = await supabase
            .from('user_distractions')
            .select('distraction_type')
            .eq('user_id', user.id)
            .eq('goal_status', 'active');

          if (distractions) {
            userDistractions = distractions.map((d: any) => d.distraction_type);
          }
        }
      } catch (err) {
        logger.error('Error fetching user distractions:', err);
      }

      const resources = await resourceMatching.matchResources(
        responseText,
        conversation,
        undefined,
        3,
        userDistractions,
        currentTopic || undefined
      );
      setMatchedResources(resources);
    } catch (error) {
      logger.error('Error matching resources:', error);
      setMatchedResources([]);
    } finally {
      setLoadingResources(false);
    }
  }

  async function handleAcceptPlan() {
    if (!pendingPlan || !currentConversationId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_action_plans')
        .insert({
          user_id: user.id,
          conversation_id: currentConversationId,
          goal: pendingPlan.goal,
          action_items: pendingPlan.actionItems,
          timeline: pendingPlan.timeline || null,
          status: 'in_progress',
          progress_percentage: 0
        });

      if (error) throw error;

      setPendingPlan(null);
      navigate('/plans');
    } catch (error) {
      logger.error('Error saving action plan:', error);
      setError('Failed to save action plan. Please try again.');
    }
  }

  function handleDismissPlan() {
    setPendingPlan(null);
  }

  async function handleSendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || aiLoading) return;

    setAiLoading(true);
    setError(null);
    const userMessage = message.trim();
    setMessage('');
    setMatchedResources([]);

    let convId = currentConversationId;
    if (!convId) {
      convId = await createNewConversation(currentTopic || undefined);
      if (!convId) {
        setError('Failed to start conversation');
        setAiLoading(false);
        return;
      }
    }

    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    saveMessageToDatabase('user', userMessage);

    try {
      setCurrentResponse('');

      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const today = new Date().toISOString().split('T')[0];
        const { error: activityError } = await supabase.rpc('update_daily_activity', {
          p_user_id: user.id,
          p_activity_date: today,
          p_coach_engaged: true
        });
        if (activityError) {
          logger.error('Error updating daily activity:', activityError);
        }
      }

      await realtimeAI.sendMessage(userMessage, conversation, undefined, user?.id);
    } catch (err: any) {
      logger.error('AI coach error:', err);
      setError(err.message || 'Failed to get coaching response');
      setAiLoading(false);
    }
  }

  function handleTopicPillClick(pill: TopicPill) {
    setCurrentTopic(pill.category);
    setMessage(pill.starter);
  }

  function startNewChat() {
    setConversation([]);
    setCurrentConversationId(null);
    setCurrentResponse('');
    setError(null);
    setMatchedResources([]);
    setCurrentTopic(null);
    setShowHistory(false);
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function getDateGroup(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return 'This Week';
    return 'Older';
  }

  function groupConversationsByDate(conversations: Conversation[]) {
    const groups: Record<string, Conversation[]> = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Older': []
    };

    conversations.forEach(conv => {
      const group = getDateGroup(conv.created_at);
      groups[group].push(conv);
    });

    return groups;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex gap-4">
        {showHistory && (
          <div className="w-72 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col" style={{ height: '680px' }}>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-ink-950">History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                >
                  Close
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setSelectedTopicFilter(null)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors ${
                    !selectedTopicFilter
                      ? 'bg-ink-950 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {TOPIC_PILLS.map(pill => (
                  <button
                    key={pill.id}
                    onClick={() => setSelectedTopicFilter(pill.category)}
                    className={`px-2 py-1 text-xs rounded-full transition-colors ${
                      selectedTopicFilter === pill.category
                        ? 'bg-ink-950 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {pill.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold-500"></div>
                </div>
              ) : conversations.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-8">No conversations yet</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupConversationsByDate(conversations)).map(([group, groupConversations]) => (
                    groupConversations.length > 0 && (
                      <div key={group}>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2 mb-2">
                          {group}
                        </h4>
                        <div className="space-y-1">
                          {groupConversations.map(conv => (
                            <button
                              key={conv.id}
                              onClick={() => loadConversation(conv.id)}
                              className={`w-full text-left p-3 rounded-lg transition-colors ${
                                currentConversationId === conv.id
                                  ? 'bg-gold-50 border border-gold-200'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <p className="text-sm font-medium text-ink-950 line-clamp-2 mb-1">
                                {conv.preview || conv.title}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{formatDate(conv.created_at)}</span>
                                {conv.message_count && conv.message_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span>{conv.message_count} msg{conv.message_count > 1 ? 's' : ''}</span>
                                  </>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col" style={{ height: '680px' }}>
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-ink-950" />
              </div>
              <div>
                <h2 className="font-semibold text-ink-950">Your Coach</h2>
                {currentTopic && (
                  <span className="text-xs text-gray-500 capitalize">{currentTopic}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`p-2 rounded-lg transition-colors ${
                  showHistory ? 'bg-gray-100 text-ink-950' : 'text-gray-500 hover:bg-gray-50'
                }`}
                title="History"
              >
                <History className="h-4 w-4" />
              </button>
              {conversation.length > 0 && (
                <button
                  onClick={startNewChat}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
                  title="New chat"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {conversation.length === 0 && !currentResponse ? (
              <div className="h-full flex flex-col items-center justify-center">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gold-100 flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-8 w-8 text-gold-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-ink-950 mb-2">What's on your mind?</h3>
                  <p className="text-gray-600 text-sm max-w-sm">
                    I'm here to help you work through challenges, set goals, and keep you accountable.
                  </p>
                </div>

                <div className="flex flex-wrap justify-center gap-2 max-w-md">
                  {TOPIC_PILLS.map(pill => (
                    <button
                      key={pill.id}
                      onClick={() => handleTopicPillClick(pill)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                        currentTopic === pill.category
                          ? 'bg-gold-500 text-ink-950'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pill.icon}
                      {pill.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {conversation.map((msg, index) => (
                  <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex items-start gap-2 max-w-[85%] ${
                      msg.role === 'user' ? 'flex-row-reverse' : ''
                    }`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-electric-400/20' : 'bg-gold-500'
                      }`}>
                        {msg.role === 'user' ? (
                          <span className="text-electric-400 font-bold text-[10px]">YOU</span>
                        ) : (
                          <MessageSquare className="h-3.5 w-3.5 text-ink-950" />
                        )}
                      </div>
                      <div className={`p-3 rounded-xl ${
                        msg.role === 'user'
                          ? 'bg-electric-400/10 text-ink-950'
                          : 'bg-gray-50 text-gray-800'
                      }`}>
                        {msg.role === 'user' ? (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <FormattedMessage content={msg.content} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {currentResponse && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2 max-w-[85%]">
                      <div className="w-7 h-7 rounded-full bg-gold-500 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="h-3.5 w-3.5 text-ink-950" />
                      </div>
                      <div className="p-3 rounded-xl bg-gray-50 text-gray-800">
                        <FormattedMessage content={currentResponse} />
                      </div>
                    </div>
                  </div>
                )}

                {aiLoading && !currentResponse && (
                  <div className="flex items-start gap-2">
                    <div className="w-7 h-7 rounded-full bg-gold-500 flex items-center justify-center">
                      <MessageSquare className="h-3.5 w-3.5 text-ink-950" />
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                      </div>
                    </div>
                  </div>
                )}

                {pendingPlan && (
                  <div className="mt-4">
                    <ActionPlanOffer
                      goal={pendingPlan.goal}
                      actionItems={pendingPlan.actionItems}
                      timeline={pendingPlan.timeline}
                      onAccept={handleAcceptPlan}
                      onDismiss={handleDismissPlan}
                    />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {error && (
            <div className="mx-4 mb-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="p-4 border-t border-gray-100">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                className="flex-1 rounded-xl border-gray-200 shadow-sm focus:border-gold-500 focus:ring-gold-500 text-sm"
              />
              <button
                type="submit"
                disabled={aiLoading || !message.trim()}
                className="px-4 py-2 bg-gold-500 text-ink-950 rounded-xl hover:bg-gold-400 disabled:opacity-50 font-medium transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>

        <ResourcesSidebar
          resources={matchedResources}
          loading={loadingResources}
          isVisible={conversation.length > 0}
          topicName={currentTopic || undefined}
        />
      </div>
    </div>
  );
}
